import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import {
  buildCountryTitleListParams,
  normalizeCountryTitleStatus,
  toCountryTitleApiPayload,
} from '../../../utils/country-title-catalog.utils';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Actividades personales por país.
 * Endpoint: `/settings/countries/{countryId}/people-activities`
 */
@Injectable({ providedIn: 'root' })
export class PersonalActivityService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const base = `${this.apiUrl}/${countryId}/people-activities`;
    return id !== undefined && id !== null ? `${base}/${id}` : base;
  }

  private mapItem(it: any): PersonalActivity {
    return {
      id: it.id,
      prefix: it.code || it.prefix || '',
      name: it.name || it.title || '',
      status: normalizeCountryTitleStatus(it.status),
      document: it.document ?? false,
      subject: it.subject ?? false,
      detail: it.detail ?? false,
    };
  }

  private toPayload(payload: any): Record<string, string | boolean> {
    const body = toCountryTitleApiPayload(payload);
    if (payload.document !== undefined) body['document'] = !!payload.document;
    if (payload.subject !== undefined) body['subject'] = !!payload.subject;
    if (payload.detail !== undefined) body['detail'] = !!payload.detail;
    return body;
  }

  getPersonalActivities(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<PersonalActivityPageResponse> {
    const params = buildCountryTitleListParams({
      page,
      size,
      sort,
      direction,
      search,
      status,
    });
    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          } as PersonalActivityPageResponse;
        }),
      );
  }

  getPersonalActivityById(countryId: string, id: string | number) {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createPersonalActivity(countryId: string, payload: any) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        this.toPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Actividad personal creada',
        })),
      );
  }

  updatePersonalActivity(countryId: string, id: string | number, payload: any) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        this.toPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Actividad personal actualizada',
        })),
      );
  }

  deletePersonalActivity(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Actividad personal eliminada',
        })),
      );
  }
}

export interface PersonalActivity {
  id: string;
  prefix: string;
  name: string;
  status?: string;
  document?: boolean;
  subject?: boolean;
  detail?: boolean;
}

export interface PersonalActivityPageResponse {
  items: PersonalActivity[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
